import { EditIcon, SmallCloseIcon, TimeIcon } from '@chakra-ui/icons';
import {
  Box,
  HStack,
  Icon,
  IconButton,
  Input,
  Link,
  Skeleton,
  Tag,
  Text,
} from '@chakra-ui/react';
import { startCase } from 'lodash';
import { useRef, useState } from 'react';

import { LinkedinIcon } from '@revelio/core';

import type { FullProfileInfoData } from '../CustomDash/components/Chart/FullProfileInfo/types';
import { useCurrencyFormatter } from './hooks/useCurrencyFormatter';

const GlobeIcon = () => (
  <Icon viewBox="0 0 24 24" boxSize={3} color="gray.400">
    <path
      fill="currentColor"
      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"
    />
  </Icon>
);

const SalaryIcon = () => (
  <Icon viewBox="0 0 24 24" boxSize={3} color="gray.400">
    <path
      fill="currentColor"
      d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"
    />
  </Icon>
);

/** Comp info: {icon} TC $xxx | Base $xxx — with inline edit for salary simulation */
export const CompInfo = ({
  profileData,
  isLoading,
  simulatedSalary,
  onSimulatedSalaryChange,
}: {
  profileData: FullProfileInfoData | null;
  isLoading: boolean;
  simulatedSalary?: number | null;
  onSimulatedSalaryChange?: (value: number | null) => void;
}) => {
  const salaryFormatter = useCurrencyFormatter();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  if (isLoading) {
    return <Skeleton height="14px" width="160px" />;
  }

  if (!profileData) return null;

  const { metadata } = profileData;
  const salaryMin = metadata.salary.min;
  const salaryMax = metadata.salary.max;
  const hasSalary = salaryMin > 0 || salaryMax > 0;

  if (!hasSalary) return null;

  const isSimulated =
    simulatedSalary !== null &&
    simulatedSalary !== undefined &&
    simulatedSalary > 0;
  const usesBase = salaryMin > 0;
  const originalSalary = usesBase ? salaryMin : salaryMax;

  const handleStartEdit = () => {
    setEditValue(String(isSimulated ? simulatedSalary : originalSalary));
    setIsEditing(true);
    setTimeout(() => inputRef.current?.select(), 0);
  };

  const handleCommit = () => {
    setIsEditing(false);
    const parsed = parseFloat(editValue);
    // biome-ignore lint/suspicious/noGlobalIsNan: migrated from develop
    if (!isNaN(parsed) && parsed > 0 && parsed !== originalSalary) {
      onSimulatedSalaryChange?.(parsed);
      // biome-ignore lint/suspicious/noGlobalIsNan: migrated from develop
    } else if (!isNaN(parsed) && parsed === originalSalary) {
      onSimulatedSalaryChange?.(null);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleCommit();
    if (e.key === 'Escape') setIsEditing(false);
  };

  const handleReset = () => {
    onSimulatedSalaryChange?.(null);
  };

  if (isEditing) {
    return (
      <HStack spacing={2}>
        <SalaryIcon />
        <Text fontSize="xs" color="gray.500" fontWeight="semibold">
          {usesBase ? 'Base' : 'TC'} (USD)
        </Text>
        <Input
          ref={inputRef}
          size="xs"
          type="number"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleCommit}
          onKeyDown={handleKeyDown}
          width="110px"
          fontWeight="bold"
          autoFocus
        />
      </HStack>
    );
  }

  if (isSimulated) {
    return (
      <HStack spacing={2}>
        <SalaryIcon />
        <Tag size="sm" colorScheme="orange" variant="subtle" fontSize="10px">
          Simulated
        </Tag>
        <Text
          fontSize="xs"
          fontWeight="bold"
          color="orange.500"
          cursor="pointer"
          onClick={handleStartEdit}
        >
          {salaryFormatter(simulatedSalary)}
        </Text>
        <Text fontSize="xs" color="gray.400" textDecoration="line-through">
          {salaryFormatter(originalSalary)}
        </Text>
        <IconButton
          aria-label="Reset salary"
          icon={<SmallCloseIcon />}
          size="xs"
          variant="ghost"
          color="gray.400"
          _hover={{ color: 'red.500' }}
          onClick={handleReset}
        />
      </HStack>
    );
  }

  return (
    <HStack spacing={2}>
      <SalaryIcon />
      {salaryMax > 0 && salaryMax !== salaryMin && (
        <HStack spacing={1}>
          <Text fontSize="xs" color="gray.500" fontWeight="semibold">
            TC
          </Text>
          <Text fontSize="xs" fontWeight="bold" color="blue.600">
            {salaryFormatter(salaryMax)}
          </Text>
        </HStack>
      )}
      {salaryMax > 0 && salaryMax !== salaryMin && salaryMin > 0 && (
        <Text fontSize="xs" color="gray.300" userSelect="none">
          |
        </Text>
      )}
      {salaryMin > 0 && (
        <HStack spacing={1}>
          <Text fontSize="xs" color="gray.500" fontWeight="semibold">
            Base
          </Text>
          <Text fontSize="xs" fontWeight="bold" color="green.600">
            {salaryFormatter(salaryMin)}
          </Text>
        </HStack>
      )}
      {onSimulatedSalaryChange && (
        <IconButton
          aria-label="Simulate salary"
          icon={<EditIcon />}
          size="xs"
          variant="ghost"
          color="gray.400"
          _hover={{ color: 'blue.500' }}
          onClick={handleStartEdit}
        />
      )}
    </HStack>
  );
};

/** Role row (for grid row 1) */
export const RoleRow = ({
  profileData,
  isLoading,
}: {
  profileData: FullProfileInfoData | null;
  isLoading: boolean;
}) => {
  if (isLoading) return <Skeleton height="14px" width="140px" />;
  if (!profileData) return <Box />;

  const { header } = profileData;

  return (
    <HStack spacing={2}>
      {header.role && (
        <Text fontSize="sm" color="gray.600" fontWeight="medium">
          {header.role}
        </Text>
      )}
      {header.profileUrl && (
        <Link
          href={header.profileUrl}
          isExternal
          display="inline-flex"
          alignItems="center"
          _hover={{ opacity: 0.7 }}
        >
          <Box as={LinkedinIcon} />
        </Link>
      )}
    </HStack>
  );
};

/** Experience row (for grid row 2) */
export const ExperienceRow = ({
  yearsOfExperience,
  isLoading,
}: {
  yearsOfExperience?: number;
  isLoading: boolean;
}) => {
  if (isLoading) return <Skeleton height="12px" width="100px" />;

  if (yearsOfExperience === undefined || yearsOfExperience <= 0) {
    return <Box />;
  }

  return (
    <HStack spacing={1}>
      <TimeIcon boxSize={3} color="gray.400" />
      <Text fontSize="xs" color="gray.500">
        {Math.round(yearsOfExperience)} years experience
      </Text>
    </HStack>
  );
};

/** Location row (for grid row 1) */
export const LocationRow = ({
  profileData,
  isLoading,
}: {
  profileData: FullProfileInfoData | null;
  isLoading: boolean;
}) => {
  if (isLoading) return <Skeleton height="12px" width="120px" />;
  if (!profileData?.header.location) return <Box />;

  return (
    <HStack spacing={1}>
      <GlobeIcon />
      <Text fontSize="xs" color="gray.500">
        {startCase(profileData.header.location)}
      </Text>
    </HStack>
  );
};

/** Email row (for grid row 2) */
export const EmailRow = ({
  profileData,
  isLoading,
}: {
  profileData: FullProfileInfoData | null;
  isLoading: boolean;
}) => {
  if (isLoading) return <Skeleton height="12px" width="100px" />;
  if (!profileData?.header.workEmail) return <Box />;

  return (
    <Link
      href={`mailto:${profileData.header.workEmail}`}
      fontSize="xs"
      color="blue.500"
    >
      {profileData.header.workEmail}
    </Link>
  );
};
